import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
} from "@/components/ui";
import { IEdoQuestionChoice } from "@/shared/model/edo-question-choice.model";
import { IEdoQuestion } from "@/shared/model/edo-question.model";
import { EdoQuestionTypeEnum } from "@/shared/model/enumerations/edo-question-type.mode";
import { getQuestionType } from "@/shared/util/question-utils";
import { cn } from "@/shared/util/cn";
import { Check, MoreVertical, Pencil, Trash2, X } from "lucide-react";
import axios from "axios";
import { useEffect, useState } from "react";
import { Translate, translate } from "react-jhipster";

interface IQuestionProps {
  question: IEdoQuestion;
  index?: number;
  className?: string;
  onEdit?: (question: IEdoQuestion) => void;
  onDelete?: (question: IEdoQuestion) => void;
}

const Question = (props: IQuestionProps) => {
  const [choices, setChoices] = useState<IEdoQuestionChoice[]>([]);
  const {
    question,
    index,
    className,
    onEdit: handleOnEdit,
    onDelete: handleOnDelete,
  } = props;

  const handleGetChoices = async () => {
    const response = await axios.get<{ data: IEdoQuestionChoice[] }>(
      `/questions/${question.id}/choices`
    );
    setChoices(response.data.data);
  };

  useEffect(() => {
    if (
      question.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
      question.type === EdoQuestionTypeEnum.TRUE_FALSE ||
      question.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE ||
      question.type === EdoQuestionTypeEnum.FILL_BLANK
    ) {
      handleGetChoices();
    }
  }, [question]);

  const renderChoices = () => {
    if (!choices?.length) {
      return (
        <p className="rounded-2xl bg-[var(--color-surface-secondary)]/40 px-3 py-3 text-center text-sm text-[var(--color-mockup-text-light)]">
          <Translate contentKey="edocendoApp.edoQuestionChoice.home.notFound">
            No Edo Question Choices found
          </Translate>
        </p>
      );
    }
    return (
      <ul className="space-y-1.5">
        {choices.map((choice) => (
          <li
            key={choice.id}
            className={cn(
              "flex items-center gap-3 rounded-2xl border border-[var(--color-mockup-border)] px-3 py-2 text-sm transition-colors dark:border-[var(--color-mockup-border)]",
              choice.isCorrect
                ? "border-primary/40 bg-primary/5 text-[var(--color-mockup-text)]"
                : "bg-surface text-[var(--color-mockup-text-mid)]"
            )}
          >
            <span
              className={cn(
                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                choice.isCorrect
                  ? "border-primary bg-primary text-white"
                  : "border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/60 text-transparent"
              )}
              aria-hidden="true"
            >
              {choice.isCorrect ? (
                <Check className="h-3 w-3" />
              ) : (
                <X className="h-3 w-3" />
              )}
            </span>
            <span className="min-w-0 flex-1 truncate">
              {choice.description}
            </span>
            <span
              className={cn(
                "shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold",
                choice.isCorrect
                  ? "bg-primary/15 text-primary"
                  : "bg-[var(--color-surface-secondary)]/60 text-[var(--color-mockup-text-light)]"
              )}
            >
              {String(choice.score)} pt
            </span>
          </li>
        ))}
      </ul>
    );
  };

  const score = question.totalScore ?? 0;

  return (
    <article
      className={cn(
        "flex flex-col gap-4 px-5 py-4 transition-colors hover:bg-[var(--color-surface-secondary)]/40 sm:px-6 sm:py-5",
        className
      )}
    >
      <div className="flex items-start gap-3">
        {typeof index === "number" && (
          <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
            {index}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[var(--color-mockup-text)] sm:text-base">
            {question.description}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--color-mockup-text-light)]">
            <span>{getQuestionType(question.type)}</span>
            <span aria-hidden="true">•</span>
            <span className="font-semibold text-[var(--color-mockup-text-mid)]">
              {score} {score === 1 ? "punto" : "punti"}
            </span>
          </div>
        </div>
        {(handleOnEdit || handleOnDelete) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                aria-label="Altre azioni"
                className="h-8 w-8 rounded-full text-[var(--color-mockup-text-light)] hover:bg-[var(--color-mockup-border)]/50 hover:text-[var(--color-mockup-text)]"
              >
                <MoreVertical className="h-4 w-4" />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              {handleOnEdit && (
                <DropdownMenuItem onClick={() => handleOnEdit(question)}>
                  <Pencil className="h-4 w-4" />
                  Modifica domanda
                </DropdownMenuItem>
              )}
              {handleOnDelete && (
                <DropdownMenuItem
                  onClick={() => handleOnDelete(question)}
                  className="text-[var(--color-mockup-coral)] focus:text-[var(--color-mockup-coral)]"
                >
                  <Trash2 className="h-4 w-4" />
                  Elimina domanda
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className={cn("space-y-3", typeof index === "number" && "pl-10")}>
        {question.type === EdoQuestionTypeEnum.FREE_TEXT && (
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[var(--color-mockup-text-light)]">
              {translate("edocendoApp.edoQuestion.description")}
            </label>
            <textarea
              name="description"
              rows={3}
              disabled
              className={cn(
                "flex min-h-[64px] w-full resize-none cursor-not-allowed rounded-2xl border border-dashed border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/40 px-3 py-2 text-sm text-[var(--color-mockup-text-mid)] dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30 dark:text-[var(--color-mockup-text-light)]"
              )}
              placeholder="Spazio per la risposta scritta dello studente"
            />
          </div>
        )}
        {question.explanationRequired && (
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[var(--color-mockup-text-light)]">
              {translate("edocendoApp.edoQuestionAnswer.explanation")}
            </label>
            <textarea
              name="explanation"
              rows={2}
              disabled
              className={cn(
                "flex min-h-[48px] w-full resize-none cursor-not-allowed rounded-2xl border border-dashed border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/40 px-3 py-2 text-sm text-[var(--color-mockup-text-mid)] dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30 dark:text-[var(--color-mockup-text-light)]"
              )}
              placeholder="Spazio per la motivazione della risposta"
            />
          </div>
        )}
        {(question.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
          question.type === EdoQuestionTypeEnum.TRUE_FALSE ||
          question.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE ||
          question.type === EdoQuestionTypeEnum.FILL_BLANK) &&
          renderChoices()}
      </div>
    </article>
  );
};

export default Question;
